/* eslint-disable no-unused-vars */
import React, { useRef } from "react";
import {
    Dialog, DialogActions, DialogContent, DialogTitle, Divider, IconButton, Paper,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Draggable from "react-draggable";
import { ResizableBox } from "react-resizable";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import PTMBottomAppBar from "./PTMBottomAppBar";
import useStore from "../store";
// eslint-disable-next-line import/no-cycle
import { PTMAppBar } from "./index";

function PaperComponent(props) {
    const nodeRef = React.useRef(null);
    const [position, setPosition] = React.useState({ x: 0, y: 0 });
    return (
        <Draggable
            nodeRef={nodeRef}
            position={position}
            handle=".ptm-dialog-draggable"
            onStop={(a, b) => {
                const tmpPos = {
                    x: b.x,
                    y: b.y,
                };

                const minY = -((b.node.parentNode.getBoundingClientRect().height - b.node.getBoundingClientRect().height) / 2);
                if (tmpPos.y < minY) {
                    tmpPos.y = minY;
                }

                const maxY = b.node.parentNode.getBoundingClientRect().height + minY - 30;
                if (tmpPos.y > maxY) {
                    tmpPos.y = maxY;
                }

                const minMaxX = ((b.node.parentNode.getBoundingClientRect().width - b.node.getBoundingClientRect().width) / 2 + b.node.getBoundingClientRect().width - 75);
                if (tmpPos.x < -minMaxX) {
                    tmpPos.x = -minMaxX;
                }

                if (tmpPos.x > minMaxX) {
                    tmpPos.x = minMaxX;
                }

                setPosition(tmpPos);
            }}
        >
            <Paper ref={nodeRef} {...props} />
        </Draggable>
    );
}

const StyledResizableBox = styled(ResizableBox)({
    "& .react-resizable-handle": {
        position: "absolute",
        width: 7,
        height: 7,
        bottom: 0,
        right: 0,
        borderRight: "2px solid rgba(0, 0, 0, 1) !important",
        borderBottom: "2px solid rgba(0, 0, 0, 1) !important",
        cursor: "se-resize",
    },
});

function ResizableWrapper(props) {
    const {
        enabled, children, dialogRef, initialWidth, initialHeight, minSizes, maxSizes, style,
    } = props;

    return enabled
        ? (
            <StyledResizableBox
                // maxConstraints={dialogRef.current ? [dialogRef.current.clientWidth - 100, dialogRef.current.clientHeight - 100] : [2000, 2000]}
                maxConstraints={maxSizes || [2000, 2000]}
                minConstraints={minSizes || [0, 0]}
                height={initialHeight}
                width={initialWidth}
                // width={!resizeable && (400)}
                style={{ overflowY: "auto", ...style }}
            >
                {children}
            </StyledResizableBox>
        )
        : children;
}

function PTMDialog(props) {
    // Visibility Conditions: open && (tabId || everywhereVisible)
    const {
        open,
        onClose,
        containerId,
        icon,
        title,
        isLoading,
        children,
        footerButtons,
        footerInfoText,
        helpName,
        draggable,
        resizeable,
        noFooter,
        initialWidth,
        initialHeight,
        fixedWidth,
        minSizes,
        maxSizes,
        disableBackdrop,
        appBarStyle,
        customDialogStyle,
        dialogWrapperStyle,
        dialogContentStyle,
        resizeableStyle,
        disableEnforceFocus,
        tabId,
        everywhereVisible,
        className,
        customPosition,
        wrapperRef,
        maxWidth,
        fullWidth,
        fullScreen,
    } = props;
    const dialogRef = useRef(null);

    const darkMode = useStore((store) => store.app.darkMode);
    const currentTab = useStore((store) => store.dashboard.currentTab);

    // const [clientWidth, setRef] = useStateRef((node) => (node?.clientWidth || 0));
    // const [width, setWidth] = React.useState(400);
    //
    // React.useEffect(() => {
    //     setWidth(clientWidth);
    // }, [clientWidth]);
    return (
        <div
            ref={wrapperRef}
            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
            tabIndex="0"
        >
            {
                (currentTab === tabId || currentTab === 0 || everywhereVisible) && (
                    <Dialog
                        disableEnforceFocus={disableEnforceFocus}
                        ref={dialogRef}
                        fullScreen={fullScreen}
                        className={className}
                        open={open}
                        PaperComponent={draggable && (PaperComponent)}
                        PaperProps={{ style: dialogWrapperStyle }}
                        onClose={(e, reason) => onClose(e, reason)}
                        maxWidth={maxWidth || "lg"}
                        fullWidth={fullWidth}
                        container={() => (containerId ? document.getElementById(containerId) : undefined)}
                        BackdropProps={{
                            style: { position: "absolute" },
                        }}
                        hideBackdrop={disableBackdrop}
                        style={{
                            position: "absolute", inset: "inherit", width: "100%", height: "100%", ...customPosition,
                        }}
                        aria-labelledby="ptm-dialog-draggable"
                        sx={{
                            displayPrint: "none",
                            "& .MuiPaper-root": {
                                margin: 0,
                            },
                            "& .MuiBackdrop-root": {
                                position: "fixed !important",
                                height: "100vh",
                            },
                            pointerEvents: disableBackdrop && "none",
                            "& .MuiDialogContent-root": {
                                width: fixedWidth ? `${fixedWidth}px !important` : "100%",
                                padding: resizeable ? 0 : 2,
                            },
                            "&>.MuiDialog-container": {
                                "&>.MuiPaper-root": {
                                    backgroundImage: "none",
                                    border: `1px solid ${darkMode ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.23)"}`,
                                    pointerEvents: disableBackdrop && "all",
                                    "&>.MuiDialogContent-root": {
                                        padding: resizeable ? 0 : 2,
                                    // width: fixedWidth ? `${fixedWidth}px !important` : "100%",
                                    },
                                },
                            },
                            ...customDialogStyle,
                        }}
                    >
                        <PTMAppBar
                            size={appBarStyle?.size ? appBarStyle?.size : "lg"}
                            style={{
                                width: fixedWidth && fixedWidth,
                                color: appBarStyle && appBarStyle.color,
                                backgroundColor: appBarStyle && appBarStyle.backgroundColor,
                                display: appBarStyle && appBarStyle.display,
                            }}
                            icon={icon}
                            title={title}
                            draggable={fullScreen ? false : draggable}
                            dragHandle="ptm-dialog-draggable"
                            controlButtons={
                                onClose ? [{
                                    title: "Schließen",
                                    color: "inherit",
                                    icon: "window-close",
                                    click: onClose,
                                }] : []
                            }
                            helpName={helpName}
                        />
                        <Divider />
                        <ResizableWrapper
                            enabled={resizeable}
                            dialogRef={dialogRef}
                            initialWidth={initialWidth}
                            initialHeight={initialHeight}
                            minSizes={minSizes}
                            maxSizes={maxSizes}
                            style={resizeableStyle}
                        >
                            <DialogContent
                                sx={
                                    {
                                        overflow: "auto",
                                        width: "fit-content",
                                        height: "fit-content",
                                        ...dialogContentStyle,
                                    }
                                }
                            >
                                {isLoading ? (
                                    <FontAwesomeIcon icon="spinner" spin />
                                ) : (
                                    children
                                )}
                            </DialogContent>
                            {!noFooter
                                && (
                                    <div>
                                        <Divider />
                                        {footerButtons?.length > 0 && (
                                            <DialogActions>
                                                <PTMBottomAppBar buttons={footerButtons} infoText={footerInfoText} />
                                            </DialogActions>
                                        )}
                                    </div>
                                )}
                        </ResizableWrapper>
                    </Dialog>
                )
            }
        </div>
    );
}

PTMDialog.propTypes = {
    draggable: PropTypes.bool,
};

PTMDialog.defaultProps = {
    draggable: true,
};

export default PTMDialog;
